"use client"

import styled from "@emotion/styled"
import { GetComponentQuery, GetPageByRouteQuery } from "@eurodrive/apollo"
import { CalculatorContent, ComparisonBox, Mobile } from "@eurodrive/ui"
import { sharedColors } from "@eurodrive/ui/theme/colors"
import {
  EngineGroup,
  FinishLevel,
  getBrands,
  PackageOption,
  transformProperties,
} from "@eurodrive/utils"
import { getUmbracoMediaUrl } from "@eurodrive/utils/getUmbracoMediaUrl"
import { Typography } from "@mui/material"
import { DetailedVehicleImage, LeasingCarDto, SaleCarDto } from "api"
import Image from "next/image"
import { useMemo, useState } from "react"
import { CarUmbraco, LeasingCarUmbraco } from "../extractUmbraco"
import SpinCarWidget from "../SpinCarWidget"
import ConfigurationSteps, { useCarConfiguration } from "./ConfigurationSteps"
import StickyFooter from "./Footer"
import Gallery from "./Gallery"

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  padding: 32px 16px 64px 16px;
  margin: auto;
  min-height: calc(100vh - 72px);
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding: 24px 120px 0 120px;
  }
`

// Figma-like navigation bar (underline active, RTL)
const ConfigNav = styled.nav`
  display: flex;
  align-items: stretch;
  gap: 10px;
  height: 60px;
`

const StyledDesktop = styled.div`
  display: none;
  ${({ theme }) => theme.breakpoints.up("md")} {
    width: 100%;
    min-width: 0;
    display: flex;
  }
`
const NavButton = styled.button<{ active: boolean }>`
  flex: 1;
  display: flex;
  gap: 10px;
  justify-content: center;
  align-items: center;
  position: relative;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0 8px 3px; /* matches figma padding (0 0 3px) inside center */
  font-family: inherit;
  font-size: 16px;
  line-height: 1.4;
  font-weight: 400;
  color: ${({ active }) => (active ? sharedColors.black : sharedColors.grey)};
  outline: none;
  transition: color 0.25s;
  &:focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }
  &:hover {
    color: ${sharedColors.black};
  }
  &::after {
    content: "";
    position: absolute;
    left: 16px;
    right: 16px;
    bottom: 0;
    height: 2px;
    background: ${({ active }) =>
      active ? sharedColors.black : "transparent"};
    border-radius: 200px;
    transition: background 0.25s;
  }
  &[disabled] {
    cursor: default;
    opacity: 0.5;
  }
`

const ContentGrid = styled.div`
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: grid;
    grid-template-columns: 1fr 372px;
    gap: 40px;
  }

  flex: 1; /* stretch inside StyledLayout */
  align-items: stretch; /* ensure sidebar cell fills height */
  min-height: 0;
`

const SidebarLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    height: 100%; /* fill grid row height on desktop only */
  }
`

const TopBox = styled.div`
  gap: 8px;
  display: flex;
  flex-direction: column;
`

interface StepRenderHelpers {
  markComplete: () => void
  completed: boolean
  active: boolean
}

export interface StepConfig {
  key: string
  label: string
  optional?: boolean
  // Render receives helpers to mark completion explicitly.
  render: (helpers: StepRenderHelpers) => React.ReactNode
}

export function formatPrice(price: number): string {
  return new Intl.NumberFormat("he-IL", {
    style: "currency",
    currency: "ILS",
    maximumFractionDigits: 0,
  }).format(price)
}

export type Config = {
  engine: null | number
  trim: null | number
  color: null | number
  finishLevel?: null | number
  packages?: number | null
}

export type EngineOption = {
  price: number
  oldPrice?: number
  engine: string
  vehicleNumber: number
  listPrice?: number
}

export type ColorOption = {
  colorCode?: number
  color: string
  label: string
  price: number
  spinCarId?: string
}

export default function CarConfiguration({
  data,
  brands,
  carUmbraco,
  leasingCarUmbraco,
  calculator,
  engineOptions,
  engineGroups = [],
  colors,
  finishLevels = [],
  additionalPackages = [],
  isLeasing = false,
  comparisonTexts,
}: {
  data: SaleCarDto | LeasingCarDto
  brands: GetComponentQuery
  carUmbraco?: CarUmbraco
  leasingCarUmbraco?: LeasingCarUmbraco
  calculator: GetPageByRouteQuery
  engineOptions: EngineOption[]
  engineGroups?: EngineGroup[]
  colors: ColorOption[]
  finishLevels?: FinishLevel[]
  additionalPackages?: PackageOption[]
  isLeasing?: boolean
  comparisonTexts?: any
}) {
  const brandsUmbraco = getBrands({ brands })

  const makeNameEn = data.carZone?.name?.makeNameEn || ""
  const carBrand = brandsUmbraco.find(
    (el) => el.title.toLowerCase() === makeNameEn.toLowerCase() ||
      el.title.toLowerCase() === data.manufacturerName.toLowerCase(),
  )
  const calculatorData = calculator.page?.properties || []
  const { blocks } = transformProperties(calculatorData)
  const calculatorContent = blocks?.blocks?.find(
    (block: any) => block.blockType === "calculatorContent",
  )

  // Determine vehicleId for tracking
  const vehicleId = isLeasing
    ? `leasing-${data.manufacturerCode}-${data.modelCode}`
    : `sale-${(data as SaleCarDto).vehicleNumber}`

  const [config, setConfig] = useState<Config>({
    engine: null,
    trim: null,
    color: null,
    finishLevel: null,
    packages: null,
  })

  // For sales cars with engineGroups, get price from selected trim
  // For leasing or backwards compatibility, use engineOptions
  const currentEngineGroup =
    !isLeasing && engineGroups.length > 0 && config.engine !== null
      ? engineGroups[config.engine]
      : null
  const currentTrim =
    currentEngineGroup && config.trim !== null
      ? currentEngineGroup.trims[config.trim]
      : null

  // Build currentOption from trim data or fall back to engineOptions
  const currentOption = currentTrim
    ? {
        price: currentTrim.price,
        oldPrice: currentTrim.oldPrice,
        engine: currentEngineGroup!.engineLabel,
        vehicleNumber: currentTrim.vehicleNumber,
      }
    : engineOptions[config.engine!]

  // Only offer colors that exist in inventory for the selected trim.
  // Falls back to the full list when there is no trim data (leasing,
  // no trim selected yet) or when filtering would leave nothing to pick
  const availableColors = useMemo(() => {
    const trimColorCodes = currentTrim?.availableColorCodes
    if (!trimColorCodes || trimColorCodes.length === 0) return colors
    const filtered = colors.filter(
      (c) => c.colorCode !== undefined && trimColorCodes.includes(c.colorCode),
    )
    return filtered.length > 0 ? filtered : colors
  }, [colors, currentTrim])

  const currentColor =
    availableColors && config.color !== null
      ? availableColors[config.color]
      : null

  const activeSpinCarId =
    currentColor?.spinCarId || data.detailedImages?.spinCarId

  const hasColors = colors && colors.length > 0

  const leasingGalleryImages: DetailedVehicleImage[] = useMemo(() => {
    if (!isLeasing || !leasingCarUmbraco) return []
    const urls: string[] = []
    const pushList = (list: any) => {
      if (Array.isArray(list)) {
        list.forEach((item) => {
          const url = typeof item === "string" ? item : item?.url
          if (url) urls.push(url)
        })
      }
    }
    pushList(leasingCarUmbraco.gallery360ImagesDesktop)
    pushList(leasingCarUmbraco.carouselPictures)
    if (leasingCarUmbraco.heroBannerImageDesktop) {
      urls.push(leasingCarUmbraco.heroBannerImageDesktop)
    }
    if (leasingCarUmbraco.vehicleInfoImage) {
      urls.push(leasingCarUmbraco.vehicleInfoImage)
    }
    const seen = new Set<string>()
    return urls
      .filter((u) => {
        if (seen.has(u)) return false
        seen.add(u)
        return true
      })
      .map((url, idx) => ({ url, label: `leasing-${idx}`, type: "" }))
  }, [isLeasing, leasingCarUmbraco])

  const leasingApiImages = data.detailedImages?.images
  const galleryPictures =
    isLeasing && (!leasingApiImages || leasingApiImages.length === 0)
      ? leasingGalleryImages
      : leasingApiImages

  const {
    activeStep,
    completed,
    setActiveStep,
    markComplete,
    clickStep,
    isStepDisabled,
    steps,
  } = useCarConfiguration({
    config,
    setConfig,
    data,
    options: engineOptions,
    engineGroups,
    colors: availableColors,
    finishLevels,
    additionalPackages,
    isLeasing,
  })

  return (
    <StyledLayout>
      <ConfigNav role="navigation" aria-label="שלבי תצורת הרכב">
        {steps.map((s, idx) => {
          const active = idx === activeStep
          const disabled = isStepDisabled(idx)
          const stepCompleted = completed[idx] || false
          return (
            <NavButton
              key={s.key}
              type="button"
              active={active}
              disabled={disabled}
              onClick={() => clickStep(idx)}
              aria-current={active ? "step" : undefined}
              aria-disabled={disabled || undefined}
              aria-label={`שלב ${idx + 1} מתוך ${steps.length}: ${s.label}${stepCompleted ? " - הושלם" : ""}`}
            >
              {stepCompleted && (
                <Image
                  src="/icons/check-black.svg"
                  alt=""
                  aria-hidden="true"
                  width={11}
                  height={7}
                />
              )}
              <span aria-hidden="true">{s.label}</span>
            </NavButton>
          )
        })}
      </ConfigNav>
      <Typography variant="body2" component={"h1"} className="sr-only">
        שלב {activeStep + 1} מתוך {steps.length}: {steps[activeStep]?.label}
      </Typography>
      <ContentGrid>
        <StyledDesktop>
          {isLeasing ? (
            galleryPictures &&
            galleryPictures.length > 0 && (
              <Gallery
                pictures={galleryPictures}
                vehicleId={vehicleId}
                bypassFilter={galleryPictures === leasingGalleryImages}
              />
            )
          ) : (
            <SpinCarWidget spinCarId={activeSpinCarId} />
          )}
        </StyledDesktop>
        <SidebarLayout>
          <TopBox>
            {carBrand && (
              <Image
                alt=""
                aria-hidden="true"
                src={getUmbracoMediaUrl(carBrand.logo)}
                width={30}
                height={30}
              />
            )}
            <Typography variant="h1" component="h2">
              {data.modelName}
            </Typography>
            <Typography variant="body2">
              {/* {data.masterModelDescription} */}
            </Typography>
          </TopBox>
          <Mobile>
            {isLeasing ? (
              galleryPictures &&
              galleryPictures.length > 0 && (
                <Gallery
                  pictures={galleryPictures}
                  vehicleId={vehicleId}
                  bypassFilter={galleryPictures === leasingGalleryImages}
                />
              )
            ) : (
              <SpinCarWidget spinCarId={activeSpinCarId} />
            )}
          </Mobile>
          <ConfigurationSteps
            activeStep={activeStep}
            completed={completed}
            markComplete={markComplete}
            steps={steps}
          />
          <StickyFooter
            activeStep={activeStep}
            completed={completed}
            config={config}
            options={engineOptions}
            engineGroups={engineGroups}
            setActiveStep={setActiveStep}
            steps={steps}
            colors={availableColors}
            finishLevels={finishLevels}
            additionalPackages={additionalPackages}
            isLeasing={isLeasing}
            data={data}
          />
        </SidebarLayout>
      </ContentGrid>
      {/* Show calculator only on the summary step */}
      {activeStep === steps.length - 1 && config.engine !== null ? (
        <CalculatorContent
          title={data.modelName}
          data={calculatorContent}
          noContainer
          fixedPrice={String(
            Number(
              (currentColor?.price || 0) +
                currentOption.price +
                (isLeasing &&
                config.finishLevel !== null &&
                config.finishLevel !== undefined &&
                finishLevels.length > 0
                  ? finishLevels[config.finishLevel]?.monthlyPrice || 0
                  : 0) +
                (isLeasing &&
                config.packages !== null &&
                config.packages !== undefined
                  ? additionalPackages[config.packages]?.monthlyPrice || 0
                  : 0),
            ),
          )}
        />
      ) : null}
      <ComparisonBox comparisonBlock={comparisonTexts?.[0]} />
    </StyledLayout>
  )
}
